/**
 * Online trouble banners: `ui.overlay = 'disconnected' | 'desynced'` (modal, the match is over), a slim
 * "RECONNECTING…" strip while the lobby socket reconnects (`ui.net`), and "WAITING FOR OPPONENT… n" while the
 * rollback session is stalled on the peer's inputs (`ui.netStall`).
 *
 * 'disconnected' has three faces: the opponent LEFT (opponent_left reason 'quit', or a forfeit verdict for us) is a
 * WIN, not an error; the opponent dropped (reason 'disconnect') is called a forfeit once the server's verdict says
 * so; a peer timeout while our own lobby socket is fine says the server is checking (its verdict follows in a few
 * seconds and turns the card into YOU WIN!); only a timeout with our socket down is a plain "CONNECTION LOST". The
 * modal ignores device presses for a
 * moment after it opens: it appears mid-play, while the player is pressing the action / jump keys.
 */
import { useEffect, useState } from 'preact/hooks';
import { useActions, useServices } from '../actions';
import { Hints } from '../components';
import { signed } from '@hf/client-core/ui-model/format';
import { blockedWhileArming, LAYER, useArming, useMenuLayer } from '../nav';
import { ui } from '@hf/client-core/ui-model/store';
import { type NetVerdict, NET_LOST, netModalCopy, stallSecondsLeft } from '@hf/client-core/ui-model/hud';
export {
  type NetVerdict,
  type NetModalCopy,
  netModalCopy,
  stallSecondsLeft,
} from '@hf/client-core/ui-model/hud';

/** The modal ignores device confirm / back / pause this long after it opens (the player was mid-play). */
export const NET_MODAL_ARM_MS = 1000;
/** A stall shorter than this is a hitch: no strip. */
export const STALL_STRIP_AFTER_MS = 400;

const nowMs = (): number => (typeof performance !== 'undefined' ? performance.now() : Date.now());

/** Server messages about the current match's end, kept for the modal (the overlay may open before or after). */
const useNetVerdict = (): NetVerdict | null => {
  const { online } = useServices();
  const [verdict, setVerdict] = useState<NetVerdict | null>(null);
  useEffect(() => {
    const keep = (matchId: string, patch: Partial<NetVerdict>): void =>
      setVerdict((v) => ({ ...(v?.matchId === matchId ? v : { matchId }), ...patch }));
    try {
      const offs = [
        online.on('opponent_left', (m) => keep(m.matchId, { left: m.reason })),
        online.on('match_result', (m) =>
          keep(m.matchId, {
            result: { status: m.status, winner: m.winner, ...(m.rating ? { rating: m.rating } : {}) },
          }),
        ),
      ];
      return () => {
        for (const off of offs) off();
      };
    } catch {
      return undefined;
    }
  }, [online]);
  return verdict;
};

export function NetBanner() {
  const overlay = ui.overlay.value;
  const net = ui.net.value;
  const setup = ui.matchSetup.value;
  const actions = useActions();
  const verdict = useNetVerdict();
  const open = overlay === 'disconnected' || overlay === 'desynced';
  const socketUp = net.status !== 'connecting' && net.status !== 'error' && net.status !== 'offline';
  const modal = open
    ? netModalCopy(overlay, verdict, setup?.online?.matchId ?? null, setup?.online?.you ?? null, socketUp)
    : null;
  const arming = useArming(open ? NET_MODAL_ARM_MS : 0, overlay);
  useMenuLayer(
    (a, source) => {
      if (source.kind !== 'ui' && !arming.ready()) {
        arming.poke();
        if (blockedWhileArming(a, source)) return;
      }
      if (a === 'confirm' || a === 'back' || a === 'pause') actions.quitMatch();
    },
    open,
    LAYER.MODAL,
  );
  if (modal)
    return (
      <div class="hf-netbanner interactive" role="alertdialog" aria-label={modal.title}>
        <div class={`hf-netbanner__card ${modal.tone}`} data-kind={modal.kind}>
          <div class="hf-netbanner__icon" aria-hidden="true">
            {modal.kind === 'win' ? <TrophyIcon /> : <NoSignalIcon />}
          </div>
          {modal.kicker ? <div class="hf-netbanner__kicker t-broadcast">{modal.kicker}</div> : null}
          <div class="hf-netbanner__title t-display">{modal.title}</div>
          <p>{modal === NET_LOST ? (net.message ?? modal.body) : modal.body}</p>
          {modal.pending ? (
            <div class="hf-netbanner__pending t-broadcast" role="status">
              <span class="hf-netstrip__spin" aria-hidden="true" />
              WAITING FOR THE SERVER'S CALL…
            </div>
          ) : null}
          {modal.rating ? (
            <div class={`hf-netbanner__elo t-broadcast ${modal.rating.delta >= 0 ? 'up' : 'down'}`}>
              RATING {modal.rating.before} → {modal.rating.after} <b>{signed(modal.rating.delta)}</b>
            </div>
          ) : null}
          <button
            class={`hf-btn interactive ${arming.armed ? '' : 'arming'}`}
            onClick={() => actions.quitMatch()}
          >
            {modal.button}
          </button>
          <Hints items={[['confirm', 'OK']]} />
        </div>
      </div>
    );
  if (ui.screen.value === 'match' && setup?.online) {
    if (net.status === 'connecting' || net.status === 'error')
      return (
        <div class="hf-netstrip t-broadcast" role="status">
          <span class="hf-netstrip__spin" aria-hidden="true" />
          {net.status === 'connecting' ? 'RECONNECTING…' : (net.message ?? 'CONNECTION PROBLEM')}
        </div>
      );
    return <StallStrip />;
  }
  return null;
}

/** "WAITING FOR OPPONENT… 3" while the session is stalled on the peer (after a short grace for hitches). */
function StallStrip() {
  const stall = ui.netStall.value;
  const [now, setNow] = useState(nowMs);
  useEffect(() => {
    if (!stall) return;
    setNow(nowMs());
    const id = setInterval(() => setNow(nowMs()), 100);
    return () => clearInterval(id);
  }, [stall]);
  if (!stall || now - stall.since < STALL_STRIP_AFTER_MS) return null;
  return (
    <div class="hf-netstrip wait t-broadcast" role="status" data-testid="stall-strip">
      <span class="hf-netstrip__spin" aria-hidden="true" />
      WAITING FOR OPPONENT…
      <b class="hf-netstrip__count">{stallSecondsLeft(stall.since, stall.timeoutMs, now)}</b>
    </div>
  );
}

function NoSignalIcon() {
  return (
    <svg viewBox="0 0 48 48">
      <path
        d="M6 18a26 26 0 0 1 36 0M12 25a17 17 0 0 1 24 0M18 32a8 8 0 0 1 12 0"
        stroke="currentColor"
        stroke-width="4"
        fill="none"
        stroke-linecap="round"
      />
      <circle cx="24" cy="39" r="3" fill="currentColor" />
      <path d="M8 8l32 32" stroke="currentColor" stroke-width="4" stroke-linecap="round" />
    </svg>
  );
}

function TrophyIcon() {
  return (
    <svg viewBox="0 0 64 80">
      <path
        d="M14 6h36v10h10c0 14-8 22-16 23-2 6-6 9-10 10v9h10v8H20v-8h10v-9c-4-1-8-4-10-10C12 38 4 30 4 16h10z"
        fill="#ffd23f"
        stroke="#140c1c"
        stroke-width="4"
        stroke-linejoin="round"
      />
      <path d="M22 12h6v20h-6z" fill="#fff6c8" opacity="0.7" />
    </svg>
  );
}
